import * as React from "react";

import { ClientViewingContext } from "@/shared/domain/agentThread/AgentThreadRecord.ts";
import { AssociationType } from "@/shared/domain/association";
import {
  getLiveVisualizerState,
  useViewingContext,
  ViewingContext,
} from "@/shared/services";

import { ContextType } from "../contextType";

/** Maps IDs to human-readable display names for the context widget */
export interface ContextDisplayNames {
  datasets: Record<string, string>;
  files: Record<string, string>;
}

interface DerivedContext {
  clientContext: ClientViewingContext;
  displayNames: ContextDisplayNames;
}

const viewingContextToDerivedContext = (
  viewingContext: ViewingContext,
): DerivedContext => {
  const datasetIds: string[] = [];
  const fileIds: string[] = [];
  const datasetDisplayNames: Record<string, string> = {};
  const fileDisplayNames: Record<string, string> = {};

  // Dataset from dataset page context
  if (viewingContext.dataset?.datasetId) {
    const id = viewingContext.dataset.datasetId;
    datasetIds.push(id);
    const record = viewingContext.dataset.record;
    if (record?.name) {
      datasetDisplayNames[id] = record.name;
    }

    if (
      viewingContext.dataset.selectedFiles &&
      viewingContext.dataset.selectedFiles.length > 0
    ) {
      viewingContext.dataset.selectedFiles.forEach((file) => {
        fileIds.push(file.file_id);
        fileDisplayNames[file.file_id] = file.relative_path;
      });
    }
  }

  // Datasets and files from visualizer context
  for (const source of viewingContext.visualizer?.dataSources ?? []) {
    if (source.type === "dataset") {
      datasetIds.push(source.id);
      if (source.record?.name) {
        datasetDisplayNames[source.id] = source.record.name;
      }
    } else if (source.type === "file") {
      fileIds.push(source.id);
      if (source.record?.relative_path) {
        fileDisplayNames[source.id] = source.record.relative_path;
      }
      // A file's association_id is the parent dataset's ID iff association_type
      // is Dataset; files can also be associated with non-Dataset entities
      // (e.g. nested file structures), so gate before pushing into datasetIds.
      if (
        source.record?.association_id &&
        source.record.association_type === AssociationType.Dataset
      ) {
        datasetIds.push(source.record.association_id);
      }
    }
  }

  if (viewingContext.file?.fileId) {
    fileIds.push(viewingContext.file.fileId);
    if (viewingContext.file.record?.relative_path) {
      fileDisplayNames[viewingContext.file.fileId] =
        viewingContext.file.record.relative_path;
    }
    if (
      viewingContext.file.record?.association_id &&
      viewingContext.file.record.association_type === AssociationType.Dataset
    ) {
      datasetIds.push(viewingContext.file.record.association_id);
    }
  }

  const dedupe = (ids: string[]) => Array.from(new Set(ids));

  return {
    clientContext: {
      dataset_ids: dedupe(datasetIds),
      file_ids: dedupe(fileIds),
      visualizer_state: viewingContext.visualizer?.state,
      misc_context: undefined,
    },
    displayNames: {
      datasets: datasetDisplayNames,
      files: fileDisplayNames,
    },
  };
};

export const useClientViewingContext = (): {
  context: ClientViewingContext;
  displayNames: ContextDisplayNames;
  handleContextDelete: (
    contextType: ContextType,
    contextContent: string,
  ) => void;
  /**
   * Resolve the ClientViewingContext for a send: the current derived context
   * (respecting the user's deletions) with live visualizer fields (e.g. the
   * playhead) folded into `visualizer_state` at call time. Live values
   * change at tick rate during playback, so they are deliberately absent
   * from the reactive `context` — call this at the moment a message is sent.
   *
   * Live fields are included only when a workspace resolver is registered
   * AND `visualizer_state` is present in the context (i.e. a visualizer is
   * in context and the user has not dismissed it).
   */
  getContextSnapshot: () => ClientViewingContext;
} => {
  const viewingContext = useViewingContext();

  const [derivedContext, setDerivedContext] = React.useState(() =>
    viewingContextToDerivedContext(viewingContext),
  );

  React.useEffect(() => {
    setDerivedContext(viewingContextToDerivedContext(viewingContext));
  }, [viewingContext]);

  const handleContextDelete = (
    contextType: ContextType,
    contextContent: string,
  ) => {
    setDerivedContext((prev) => {
      if (contextType === "dataset") {
        return {
          ...prev,
          clientContext: {
            ...prev.clientContext,
            dataset_ids: prev.clientContext.dataset_ids.filter(
              (id) => id !== contextContent,
            ),
          },
        };
      } else if (contextType === "file") {
        return {
          ...prev,
          clientContext: {
            ...prev.clientContext,
            file_ids: prev.clientContext.file_ids.filter(
              (id) => id !== contextContent,
            ),
          },
        };
      } else if (contextType === "viz_state") {
        return {
          ...prev,
          clientContext: {
            ...prev.clientContext,
            visualizer_state: undefined,
          },
        };
      }
      return prev;
    });
  };

  // A plain per-render closure (like handleContextDelete above): callers
  // invoke it inside event handlers, so no consumer depends on a stable
  // identity, and closing over this render's derivedContext reads the same
  // deletion-filtered state the reactive `context` exposes.
  const getContextSnapshot = (): ClientViewingContext => {
    const clientContext = derivedContext.clientContext;
    // No visualizer in context (or the user dismissed it) — nothing to
    // enrich. Returning the context unchanged also keeps live state out of
    // messages the user explicitly scoped away from the visualizer.
    if (clientContext.visualizer_state === undefined) {
      return clientContext;
    }
    const liveState = getLiveVisualizerState();
    if (liveState === undefined) {
      return clientContext;
    }
    return {
      ...clientContext,
      visualizer_state: {
        ...clientContext.visualizer_state,
        ...liveState,
      },
    };
  };

  return {
    context: derivedContext.clientContext,
    displayNames: derivedContext.displayNames,
    handleContextDelete,
    getContextSnapshot,
  };
};
